import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Checkbox,
  Slider,
  Toggle,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import classNames from 'classnames';
import uiDefault from '../../apollo/uiDefault';
import { convertIntToNth } from '../../utils/helpers/formatting';
import { secondsToTime } from '../../utils/helpers/time';
import {
  uiState,
  cf_Quarter,
  cf_QuarterEnabled,
  cf_Clock,
  cf_ClockEnabled,
} from '../../apollo';
import {
  FilterRoundel,
  FiltersHR,
  FilterDynamicValue,
  ModalWidth,
  TriggerButtonContainer,
} from './ContextFilters.styles';
import { enableFilter } from './Filters.helper';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { FormField, FormGroupEqual } from '../../styles/global.styles';
import { clockFilterLabels } from './TimeFilter.constants';

const TimeFilter = ({ handleEnabledToggle }) => {
  // modal open/close
  const [st_ModalOpen, setModalOpen] = useState(false);
  // context filters in global state
  const cf_QuarterRV = useReactiveVar(cf_Quarter);
  const cf_QuarterEnabledRV = useReactiveVar(cf_QuarterEnabled);
  const cf_ClockRV = useReactiveVar(cf_Clock);
  const cf_ClockEnabledRV = useReactiveVar(cf_ClockEnabled);
  // context filters in local state
  const [st_Quarter, setQuarter] = useState(cf_QuarterRV);
  const [st_Clock, setClock] = useState(cf_ClockRV);

  const handleQuarter = (isQuarterChecked, index) => {
    const quarters = [...st_Quarter];
    quarters[index] = isQuarterChecked;
    setQuarter([...quarters]);
    enableFilter(cf_QuarterEnabledRV, cf_QuarterEnabled, 'quarter');
  };

  const handleClockPosition = (evt, sliderValues) => {
    setClock([...sliderValues]);
  };

  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleReset = () => {
    const { quarter, clock } = uiDefault.contextFilters;

    setQuarter([...quarter.value]);
    cf_QuarterEnabled(quarter.enabled);
    setClock([...clock.value]);
    cf_ClockEnabled(clock.enabled);

    uiState().contextFilters.fieldPosition.value = [...quarter.value];
    uiState().contextFilters.fieldPosition.enabled = quarter.enabled;
    uiState().contextFilters.clock.value = [...clock.value];
    uiState().contextFilters.clock.enabled = clock.enabled;

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const handleModalClose = () => {
    const { contextFilters } = uiState();
    const { quarter, clock } = contextFilters;

    cf_Quarter([...st_Quarter]);
    quarter.value = [...st_Quarter];
    cf_QuarterEnabled(cf_QuarterEnabledRV);

    // convert from seconds to milliseconds
    cf_Clock([st_Clock[0], st_Clock[1]]);
    clock.value = [...st_Clock];
    cf_ClockEnabled(cf_ClockEnabledRV);

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
    setModalOpen(false);
  };

  const filterCount = [cf_QuarterEnabledRV, cf_ClockEnabledRV].filter(
    Boolean
  ).length;
  const isEligiblePage = useIsEligiblePage('TIME');
  const isEnabled = filterCount > 0 && isEligiblePage;

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage}
          onClick={handleOpen}
        >
          Time
        </Button>
        {isEnabled && <FilterRoundel>{filterCount}</FilterRoundel>}
      </TriggerButtonContainer>

      <Modal onCancel={handleModalClose} isOpen={st_ModalOpen}>
        <ModalWidth $minWidth="570px">
          <ModalHeader>Time</ModalHeader>
          <ModalBody>
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="quarter-cf-toggle"
                label="Quarter"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_QuarterEnabledRV,
                    cf_QuarterEnabled,
                    'quarter',
                    {
                      enabled: !cf_QuarterEnabledRV,
                    }
                  )
                }
                checked={cf_QuarterEnabledRV}
                size="small"
              />
            </FormField>
            <FormGroupEqual>
              {st_Quarter &&
                st_Quarter.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={`quarters-field-${index}`}>
                    <Checkbox
                      id={`quarters-field-${index}-checkbox`}
                      label={
                        index < 4 ? convertIntToNth(index + 1) : 'Overtime'
                      }
                      onChange={() => handleQuarter(!item, index)}
                      value={String(st_Quarter[index])}
                      checked={item}
                      size="small"
                      disabled={!cf_QuarterEnabledRV}
                    />
                  </div>
                ))}
            </FormGroupEqual>
            <FiltersHR $margin="1.25rem 1rem 0.75rem" />
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="clock-cf-toggle"
                label="Clock"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_ClockEnabledRV,
                    cf_ClockEnabled,
                    'clock',
                    {
                      enabled: !cf_ClockEnabledRV,
                    }
                  )
                }
                checked={cf_ClockEnabledRV}
                size="small"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_ClockEnabledRV,
                })}
              >
                {st_Clock &&
                  `${secondsToTime(st_Clock[1])} — ${secondsToTime(
                    st_Clock[0]
                  )}`}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0.5rem">
              <Slider
                id="clock-context-filter-slider"
                labels={clockFilterLabels}
                min={0}
                max={900}
                onChange={handleClockPosition}
                presets={[
                  {
                    name: 'Inside 2:00',
                    value: [0, 120],
                  },
                ]}
                isDisabled={!cf_ClockEnabledRV}
                variant="greater-than"
                value={st_Clock}
                step={10}
                version={2}
              />
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={handleModalClose}>Save</Button>
          </ModalFooter>
        </ModalWidth>
      </Modal>
    </>
  );
};

TimeFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default TimeFilter;
